import React, { MouseEvent, useMemo, useState, useCallback, useRef } from 'react';

import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Box, Typography } from '@mui/material';

import {
    useSociete,
    ReminderPublisher,
    ReminderPublisherStatus,
    AchatsStateType,
    useAchatsService,
    TiersAppDispatch,
} from '@europrocurement/l2d-domain';
import {
    ChipStatus,
    HeaderActionButton,
    ListHeader,
    SkeletonLoader,
    ListSkeleton,
    EmptyBox,
    HeaderListSkeleton,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { DataSource } from '@europrocurement/l2d-redux-utils';

import { createReminderPublisher as createReminderPublisherIcon } from '@europrocurement/l2d-icons';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import PublishersRemindersActionMenu from './PublishersRemindersActionMenu';
import { ReminderPublisherModal } from '../detail/PublisherReminder/components/ReminderPublisherModal';
import useSendMailReminderPublisher from '../detail/PublisherReminder/hooks/useSendMailReminderPublisher';
import useDeleteReminderPublisher from '../detail/PublisherReminder/hooks/useDeleteReminderPublisher';
import { AVAILABLE_SOCIETIES } from '../detail/PublisherReminder/constants/societyData';

/**
 * Page de listing des rappels fournisseurs.
 */
const PublishersRemindersListing: React.FunctionComponent = function () {
    const dispatch = useDispatch<TiersAppDispatch>();

    const reminderPublisherDataSource: DataSource<ReminderPublisher> = useSelector(
        (s: AchatsStateType) => s.achats.reminderPublisher.main,
        _.isEqual,
    );

    const { getReminderPublisher } = useAchatsService();
    const { isInitialLoading, isInError } = useInitialLoadingState(reminderPublisherDataSource);

    // Chargement initial des données
    React.useEffect(() => {
        dispatch(getReminderPublisher({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const location = useLocation();
    const navigate = useNavigate();
    const xIdSociete = useSociete();

    const { sendMultipleMailReminderPublisher } = useSendMailReminderPublisher({ from: 'list' });
    const { deleteMultipleReminderPublisher } = useDeleteReminderPublisher({ from: 'list' });

    // Gestion de la modal via ref
    const modalRef = useRef<{ openModal: () => void }>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleOpenModal = useCallback(() => {
        modalRef.current?.openModal();
    }, []);

    const handleProcessingStart = useCallback(() => {
        setIsProcessing(true);
    }, []);

    const handleProcessingEnd = useCallback(() => {
        setIsProcessing(false);
    }, []);

    const columns: ColumnDatatable<ReminderPublisher>[] = useMemo(
        () => [
            {
                label: 'Fournisseur',
                render: ({ fournisseurNom }) => (
                    <Typography variant="l2dtableCell">{fournisseurNom}</Typography>
                ),
                isDisplayed: true,
                field: 'fournisseurNom',
            },
            {
                label: 'Destinataire',
                render: ({ fournisseurEmail }) => (
                    <Typography variant="l2dtableCell">{fournisseurEmail}</Typography>
                ),
                isDisplayed: true,
                field: 'fournisseurEmail',
            },
            {
                label: 'Nombre de factures associées',
                render: ({ reminderPublisherFactures }) => (
                    <Typography variant="l2dtableCell">
                        {reminderPublisherFactures?.length}
                    </Typography>
                ),
                isDisplayed: true,
                field: 'reminderPublisherFactures',
            },
            {
                label: 'Statut',
                render: ({ status }) => <ChipStatus status={status} />,
                isDisplayed: true,
                sortable: true,
                field: 'status',
            },
            {
                label: 'Lié à la société',
                render: ({ idSociete }) => {
                    const societyLabel = AVAILABLE_SOCIETIES.find((society) => {
                        if (!idSociete) return null;
                        return society.id.toString() === idSociete.toString();
                    })?.label;

                    return idSociete && societyLabel ? (
                        <Typography variant="l2dtableCell">{societyLabel}</Typography>
                    ) : (
                        <Typography
                            variant="l2dtableCell"
                            color="GrayText"
                        >
                            Pas de société
                        </Typography>
                    );
                },
                isDisplayed: true,
                sortable: true,
                field: 'idSociete',
            },
            {
                label: 'Créé le',
                render: ({ createdAt }) => {
                    const publisherReminderCreationDate = createdAt
                        ? new Date(Date.parse(createdAt)).toLocaleString()
                        : null;

                    return (
                        <Typography variant="l2dtableCell">
                            {publisherReminderCreationDate}
                        </Typography>
                    );
                },
                isDisplayed: true,
                sortable: true,
                field: 'createdAt',
            },
            {
                label: 'Créé par',
                render: ({ createdBy }) => (
                    <Typography variant="l2dtableCell">{createdBy}</Typography>
                ),
                isDisplayed: true,
            },
            {
                label: 'Mis à jour le',
                render: ({ updatedAt }) => {
                    const publisherReminderUpdateDate = updatedAt
                        ? new Date(Date.parse(updatedAt)).toLocaleString()
                        : null;

                    return (
                        <Typography variant="l2dtableCell">
                            {publisherReminderUpdateDate}
                        </Typography>
                    );
                },
                isDisplayed: false,
                sortable: true,
                field: 'updatedAt',
            },
            {
                label: 'Mis à jour par',
                render: ({ updatedBy }) => (
                    <Typography variant="l2dtableCell">{updatedBy}</Typography>
                ),
                isDisplayed: false,
            },
            {
                label: 'Actions',
                onClickCell: () => {},
                render: (reminderPublisher: ReminderPublisher) => (
                    <PublishersRemindersActionMenu reminderPublisher={reminderPublisher} />
                ),
                isDisplayed: true,
            },
        ],
        [],
    );

    const filters: FiltersDatatableList = useMemo(
        () => [
            {
                field: 'statuses',
                label: 'status',
                type: 'staticSelect',
                options: Object.entries(ReminderPublisherStatus).map(([key, value]) => ({
                    value: key,
                    label: value as string,
                })),
            },
            {
                field: 'libelle',
                label: 'libelle',
                type: 'text',
            },
        ],
        [],
    );

    const errorContent = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des données."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <>
            <ReminderPublisherModal
                ref={modalRef}
                fetchDataAfterSuccess={getReminderPublisher}
                onProcessingStart={handleProcessingStart}
                onProcessingEnd={handleProcessingEnd}
            />

            <SkeletonLoader
                isLoading={isInitialLoading}
                skeleton={
                    <>
                        <HeaderListSkeleton />
                        <ListSkeleton
                            showSearch
                            columns={6}
                        />
                    </>
                }
                isInError={isInError}
                errorChildren={errorContent}
            >
                <ListHeader
                    breadcrumbPath={[
                        { path: 'Fournisseurs', link: '/fournisseurs/liste' },
                        'Liste des rappels',
                    ]}
                    actions={
                        <HeaderActionButton
                            label="Créer un rappel fournisseur"
                            icon={createReminderPublisherIcon}
                            onClick={handleOpenModal}
                            disabled={isProcessing}
                            actionType="modal"
                        />
                    }
                />

                {reminderPublisherDataSource && (
                    <StoreDatatable
                        dataSource={reminderPublisherDataSource}
                        columns={columns}
                        fetchData={getReminderPublisher}
                        filters={filters}
                        localStorageKey="FlexyStoreDatatablePublishersReminderList"
                        onClickRow={(__: MouseEvent<HTMLElement>, { id }: ReminderPublisher) =>
                            navigate(`${id}`)
                        }
                        onWheelClickRow={(
                            ___: MouseEvent<HTMLElement>,
                            { id }: ReminderPublisher,
                        ) =>
                            window.open(
                                `${location.pathname}/${id}`,
                                '_blank',
                                'rel=noopener noreferrer',
                            )
                        }
                        observers={[xIdSociete]}
                        fetchWithoutFilter
                        rowsActions={[
                            {
                                icon: 'fa-paper-plane',
                                label: 'Envoyer les mails',
                                onClick: (reminderPublishers: Array<ReminderPublisher>) => {
                                    sendMultipleMailReminderPublisher(reminderPublishers);
                                },
                            },
                            {
                                icon: 'fa-trash-can',
                                label: 'Supprimer les rappels',
                                onClick: (reminderPublishers: Array<ReminderPublisher>) => {
                                    deleteMultipleReminderPublisher(reminderPublishers);
                                },
                            },
                        ]}
                        showSearch
                    />
                )}
            </SkeletonLoader>
        </>
    );
};

export default PublishersRemindersListing;
